import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Instagram, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "@/constants/site";
import { CONTACT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", sport: "", level: "", message: "" };

const ContactForm = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Name, email and message are required.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/leads`, form);
      setDone(true);
      setForm(initial);
      toast.success("Message received — check your inbox.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Something went wrong. Try again.";
      setError(typeof msg === "string" ? msg : "Submission failed.");
      toast.error("Submission failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white/[0.03] border border-white/10 px-4 py-4 text-white placeholder:text-zinc-600 font-mono-plex text-sm tracking-wide focus:outline-none focus:border-[#FF4D00] focus:bg-white/[0.06]";

  if (done) {
    return (
      <motion.div
        data-testid={CONTACT.success}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="border border-white/10 bg-[#111] p-8 md:p-12"
      >
        <CheckCircle2 size={40} className="text-[#FF4D00] mb-6" />
        <h3 className="font-display uppercase text-4xl md:text-5xl leading-none">
          Message received.
        </h3>
        <p className="mt-4 text-zinc-400 max-w-[52ch] leading-relaxed">
          You'll hear back within 24–48 hours. In the meantime, slide into the
          DMs on Instagram and tell us your sport, grade, and biggest goal for
          the next 12 months.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={SITE.instagramUrl}
            target="_blank"
            rel="noreferrer"
            className="btn-fire"
          >
            DM {SITE.instagramHandle}
            <Instagram size={16} />
          </a>
          <button
            type="button"
            onClick={() => setDone(false)}
            className="btn-ghost"
          >
            Send Another
            <ArrowUpRight size={16} />
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <form
      data-testid={CONTACT.form}
      onSubmit={submit}
      className="border border-white/10 bg-[#0F0F0F] p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-4"
      noValidate
    >
      <div className="md:col-span-2 flex items-center justify-between mb-2">
        <div className="num-tag">/ LEAD FORM · 05</div>
        <div className="font-mono-plex text-[10px] tracking-[0.28em] text-zinc-500 uppercase">
          Encrypted
        </div>
      </div>

      <label className="md:col-span-1 block">
        <span className="block font-mono-plex text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-2">
          / Name
        </span>
        <input
          type="text"
          value={form.name}
          onChange={onChange("name")}
          data-testid={CONTACT.name}
          placeholder="First & last name"
          className={inputCls}
          required
        />
      </label>

      <label className="md:col-span-1 block">
        <span className="block font-mono-plex text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-2">
          / Email
        </span>
        <input
          type="email"
          value={form.email}
          onChange={onChange("email")}
          data-testid={CONTACT.email}
          placeholder="you@domain.com"
          className={inputCls}
          required
        />
      </label>

      <label className="md:col-span-1 block">
        <span className="block font-mono-plex text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-2">
          / Sport
        </span>
        <input
          type="text"
          value={form.sport}
          onChange={onChange("sport")}
          data-testid={CONTACT.sport}
          placeholder="Football · Basketball · Track…"
          className={inputCls}
        />
      </label>

      <label className="md:col-span-1 block">
        <span className="block font-mono-plex text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-2">
          / Level
        </span>
        <input
          type="text"
          value={form.level}
          onChange={onChange("level")}
          data-testid={CONTACT.level}
          placeholder="High School · College · Pro"
          className={inputCls}
        />
      </label>

      <label className="md:col-span-2 block">
        <span className="block font-mono-plex text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-2">
          / What are you trying to build?
        </span>
        <textarea
          value={form.message}
          onChange={onChange("message")}
          data-testid={CONTACT.message}
          placeholder="Tell us your goals — sport, school, brand, NIL…"
          rows={5}
          className={`${inputCls} resize-none`}
          required
        />
      </label>

      {error && (
        <div
          data-testid={CONTACT.error}
          className="md:col-span-2 border border-red-500/30 bg-red-500/10 text-red-300 px-4 py-3 text-sm"
        >
          {error}
        </div>
      )}

      <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-4 pt-2">
        <p className="text-xs text-zinc-500 max-w-[38ch]">
          By submitting, you agree to be contacted by TWGAS Athletics.
        </p>
        <button
          type="submit"
          data-testid={CONTACT.submit}
          disabled={submitting}
          className="btn-fire disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? "Sending…" : "Send It"}
          <ArrowUpRight size={18} />
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
